import { Link } from "react-router-dom";
import { Mail, ArrowUpRight, MapPin, MessageCircle } from "lucide-react";
import logo from "@/assets/nivexa_icon_80.webp";
import bgFooter from "@/assets/bg-footer.jpg";

const Footer = () => {
  return (
    <footer className="relative border-t border-border/20 overflow-hidden">
      {/* Background image */}
      <img
        src={bgFooter}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
        loading="lazy"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/95 to-background/80 pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative">
        {/* Top section with brand */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-12 pb-10 border-b border-border/20">
          <div className="space-y-3 max-w-sm">
            <Link to="/" className="flex items-center gap-2.5">
              <img src={logo} alt="Nivexa FX Logo" width={64} height={64} className="h-14 w-14 object-contain" loading="lazy" />
              <span className="font-display font-bold text-lg tracking-wide text-foreground">NivexA <span className="text-primary">FX</span></span>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed">
              A global financial technology company focused on developing intelligent trading software and analytical tools.
            </p>
            {/* Dubai Location */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary shrink-0" />
              <span className="text-2xl leading-none">🇦🇪</span>
              <span>Dubai, United Arab Emirates</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="mailto:support@nivexafx.com"
              className="h-10 w-10 rounded-full border border-border/40 bg-secondary/30 flex items-center justify-center hover:border-primary/40 hover:bg-primary/10 transition-all"
              aria-label="Email"
            >
              <Mail className="h-4 w-4 text-muted-foreground" />
            </a>
          </div>
        </div>

        {/* Links grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Product */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Product</h3>
            <div className="flex flex-col gap-2.5">
              {[
                { to: "/product", label: "RapidX EA" },
                { to: "/pricing", label: "Pricing" },
                { to: "/blog", label: "Education Hub" },
                { to: "/faq", label: "FAQ" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                  {l.label}
                  <ArrowUpRight className="h-3 w-3 opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-y-0 transition-all" />
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Legal</h3>
            <div className="flex flex-col gap-2.5">
              {[
                { to: "/legal/risk-disclaimer", label: "Risk Disclaimer" },
                { to: "/legal/terms", label: "Terms & Conditions" },
                { to: "/legal/privacy", label: "Privacy Policy" },
                { to: "/legal/refund", label: "Refund Policy" },
                { to: "/legal/cookies", label: "Cookie Policy" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 group">
                  {l.label}
                  <ArrowUpRight className="h-3 w-3 opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-y-0 transition-all" />
                </Link>
              ))}
            </div>
          </div>

          {/* Support */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Support</h3>
            <div className="flex flex-col gap-2.5">
              <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5 text-primary" />
                Live Chat (via website)
              </span>
              <a href="mailto:support@nivexafx.com" className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-primary" />
                support@nivexafx.com
              </a>
            </div>
          </div>

          {/* Location & Stay Updated */}
          <div className="space-y-4">
            <h3 className="font-display font-semibold text-xs uppercase tracking-widest text-foreground/70">Global Presence</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="text-lg">🇦🇪</span>
              <span>Dubai, UAE</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Serving traders worldwide with cutting-edge trading automation technology from the heart of the Middle East's financial hub.
            </p>
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium">
              Education Hub <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border/20 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground/60">
              © {new Date().getFullYear()} Nivexa FX. All rights reserved.
            </p>
            <span className="text-xs text-muted-foreground/40">•</span>
            <span className="text-xs text-muted-foreground/50 flex items-center gap-1">
              🇦🇪 Dubai, UAE
            </span>
          </div>
          <p className="text-xs text-muted-foreground/50 text-center max-w-xl leading-relaxed">
            Trading involves substantial risk. Past performance does not guarantee future results. This is not financial advice.
          </p>
        </div>
        <div className="text-center mt-4">
          <p className="text-xs text-muted-foreground/40">
            Website developed & managed by{" "}
            <a href="https://arionastudio.com/" target="_blank" rel="noopener noreferrer" className="text-primary/60 hover:text-primary transition-colors">
              Ariona Studio
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
